/* Landing hero v2 — user's sunset photo (woman + golden retriever).
   Card ends exactly at the bottom of the meadow; hook + CTAs sit in the sky
   above the two subjects. Sky headroom on wide/tall screens is filled by a
   blurred copy of the same photo so the image itself is never distorted. */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/context/AuthContext";
import { useInstall, PLAY_STORE_URL } from "@/context/InstallContext";
import { trackInstallEvent } from "@/lib/installTrack";
import { InstallInstructionsModal } from "@/components/InstallPrompt";

const IMG = "/landing/hero-sunset.webp?v=1";
const IMG_SM = "/landing/hero-sunset-1100.webp?v=1";
const IMG_BLUR = "/landing/hero-sunset-blur.webp?v=1";

const MASK = "linear-gradient(180deg, transparent 0%, #000 22%)";

const fade = (d) => ({ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay: d, ease: [0.22, 1, 0.36, 1] } });

function PlayLogo() {
  return (
    <svg viewBox="0 0 24 26" className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden>
      <path d="M1 1.2 13.6 13 1 24.8c-.4-.2-.6-.7-.6-1.2V2.4c0-.5.2-1 .6-1.2Z" fill="#00D7FE" />
      <path d="M17.8 8.9 13.6 13 1 1.2c.3-.2.8-.2 1.3 0l15.5 7.7Z" fill="#00F076" />
      <path d="M17.8 17.1 2.3 24.8c-.5.3-1 .2-1.3 0L13.6 13l4.2 4.1Z" fill="#FF3A44" />
      <path d="m22.6 14.6-4.8 2.5-4.2-4.1 4.2-4.1 4.8 2.5c1.2.6 1.2 2.6 0 3.2Z" fill="#FFD400" />
    </svg>
  );
}

function AppleLogo() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 sm:h-6 sm:w-6" fill="white" aria-hidden>
      <path d="M16.37 1.43c0 1.14-.5 2.27-1.18 3.08-.74.9-1.99 1.57-2.99 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.57-2.27 1.2-2.98.81-.94 2.15-1.64 3.25-1.68.03.13.06.28.06.43Zm4.56 15.71c-.03.07-.46 1.58-1.52 3.12-.94 1.34-1.94 2.71-3.43 2.71-1.52 0-1.9-.88-3.63-.88-1.7 0-2.3.91-3.67.91-1.38 0-2.33-1.26-3.43-2.8C3.96 18.38 2.93 15.57 2.93 12.92c0-4.28 2.8-6.55 5.55-6.55 1.45 0 2.67.95 3.6.95.86 0 2.22-1.01 3.9-1.01.61 0 2.89.06 4.37 2.19-.13.09-2.38 1.37-2.38 4.19 0 3.26 2.85 4.42 2.95 4.45Z" />
    </svg>
  );
}

function StoreBadge({ icon, kicker, label, testid, ...rest }) {
  const Tag = rest.href ? "a" : "button";
  return (
    <Tag data-testid={testid} {...rest}
      className="group inline-flex items-center gap-1.5 sm:gap-2.5 h-[50px] sm:h-[54px] pl-2.5 pr-3 sm:pl-3.5 sm:pr-5 rounded-2xl bg-[#0F0F0F] text-white border border-white/10 transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.97]"
      style={{ boxShadow: "0 14px 30px -14px rgba(0,0,0,0.55)" }}>
      {icon}
      <span className="flex flex-col items-start leading-none text-left">
        <span className="text-[8.5px] sm:text-[9.5px] uppercase tracking-[0.12em] text-white/70 whitespace-nowrap">{kicker}</span>
        <span className="text-[14px] sm:text-[17px] font-semibold mt-1 tracking-tight whitespace-nowrap">{label}</span>
      </span>
    </Tag>
  );
}

function QrCard() {
  const [open, setOpen] = useState(true);
  if (!open) return null;
  return (
    <motion.div {...fade(0.9)} data-testid="hero-qr-card"
      className="hidden lg:flex absolute right-5 bottom-5 z-20 flex-col items-center gap-2 p-4 pb-3 rounded-[22px] bg-white/95 backdrop-blur"
      style={{ boxShadow: "0 20px 40px -18px rgba(0,0,0,0.35)" }}>
      <button onClick={() => setOpen(false)} aria-label="Chiudi" data-testid="hero-qr-close"
        className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full flex items-center justify-center text-[#9A9A9A] hover:bg-[#F4EFE6]">
        <X className="h-3.5 w-3.5" />
      </button>
      <QRCodeSVG value="https://oriodog.com/?src=qr" size={104} fgColor="#1A1A1A" level="M" />
      <span className="text-[12px] font-semibold text-[#1A1A1A]">Inquadra per scaricare</span>
      <span className="-mt-1.5 text-[10.5px] text-[#9A9A9A]">Android e iPhone</span>
    </motion.div>
  );
}

export default function LandingHero() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { platform } = useInstall();
  const [iosHelp, setIosHelp] = useState(false);
  const isIOS = platform === "ios";

  const startFree = () => {
    if (user) return navigate("/app");
    trackInstallEvent("register_opened");
    navigate("/register");
  };
  const play = (
    <StoreBadge key="play" testid="hero-play-badge" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer"
      onClick={() => trackInstallEvent("play_store_clicked")}
      icon={<PlayLogo />} kicker="Disponibile su" label="Google Play" />
  );
  const apple = (
    <StoreBadge key="ios" testid="hero-ios-badge" type="button"
      onClick={() => { trackInstallEvent("ios_install_opened"); setIosHelp(true); }}
      icon={<AppleLogo />} kicker="Su iPhone" label="Installa dal sito" />
  );

  return (
    <section data-testid="hero-section" className="relative z-10">
      <div className="relative overflow-hidden orio-hero-h">
        <div aria-hidden className="absolute -inset-10"
          style={{ backgroundImage: `url('${IMG_BLUR}')`, backgroundSize: "cover", backgroundPosition: "center bottom", filter: "blur(28px) saturate(1.1)" }} />
        <img data-testid="hero-desktop-image" src={IMG} srcSet={`${IMG_SM} 1100w, ${IMG} 1663w`} sizes="(max-width: 640px) 900px, 100vw"
          alt="Ragazza con il suo golden retriever al tramonto mentre usa ORIO" fetchpriority="high" decoding="async"
          className="absolute bottom-0 left-[47%] -translate-x-1/2 max-w-none w-auto h-[58%] sm:left-0 sm:translate-x-0 sm:w-full sm:h-auto pointer-events-none select-none"
          style={{ WebkitMaskImage: MASK, maskImage: MASK }} />
        <div aria-hidden className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(60% 42% at 50% 26%, rgba(255,250,244,0.62) 0%, rgba(255,250,244,0) 100%)" }} />

        <div className="relative z-10 h-full flex flex-col items-center text-center px-5 pt-24 sm:pt-28">
          <motion.div {...fade(0.05)}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 backdrop-blur border border-white/70 text-[9px] sm:text-[10px] uppercase tracking-[0.14em] sm:tracking-[0.22em] text-[#5C5C5C] whitespace-nowrap">
            <span className="h-1.5 w-1.5 rounded-full bg-[#34D399] animate-pulse" /> Piattaforma italiana per il tuo cane
          </motion.div>
          <motion.h1 {...fade(0.12)} data-testid="hero-title"
            className="mt-4 font-display font-bold tracking-tighter leading-[1.02] text-[#141414] text-[2.35rem] sm:text-6xl lg:text-[68px]">
            Con ORIO,<br className="sm:hidden" /> il tuo cane<br />
            <span style={{ background: "linear-gradient(120deg,#FF5C2A 0%,#FF7A1A 45%,#E0901A 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
              non è mai solo.
            </span>
          </motion.h1>
          <motion.p {...fade(0.2)} className="mt-4 max-w-xl text-[14.5px] sm:text-[17px] leading-relaxed text-[#3A3330]">
            Radar pericoli, SOS, cassetto sanitario con AI, spese e compagni di passeggiata.
            <span className="hidden sm:inline"> Tutto in un&apos;unica app, gratis per iniziare.</span>
          </motion.p>
          <motion.div {...fade(0.28)} className="mt-6 flex flex-wrap items-center justify-center gap-2 sm:gap-2.5">
            {isIOS ? [apple, play] : [play, apple]}
          </motion.div>
          <motion.button {...fade(0.36)} onClick={startFree} data-testid="hero-cta-btn"
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/75 backdrop-blur text-[13px] font-semibold text-[#1A1A1A] border border-white/80 transition-transform hover:-translate-y-0.5">
            <Sparkles className="h-3.5 w-3.5 text-[#FF7A1A]" />
            {user ? "Apri ORIO" : <>30 giorni Pro gratis<span className="hidden sm:inline"> · inizia dal browser</span><span className="sm:hidden"> · inizia ora</span></>} <ArrowRight className="h-3.5 w-3.5" />
          </motion.button>
        </div>
        <QrCard />
      </div>
      {iosHelp && <InstallInstructionsModal platform="ios" onClose={() => setIosHelp(false)} />}
    </section>
  );
}
